import React from 'react';
import type { Node } from 'react';
import { themeProp } from '@vmos2/utils';
import styled from '@emotion/styled';
import some from 'lodash/some';

import { useComponentTheme } from '../useComponentTheme';

type Props = {
  ariaLabel?: string,
  children?: Node,
  topNav?: Node | string,
  className?: string,
  hasShadow?: boolean,
  notification?: Node | string,
  mode?: string,
  backgroundColor?: string,
  kioskAuth?: Node | string,
  navAlignment?: 'left' | 'right',
};

export const Header = ({
  ariaLabel,
  children,
  topNav,
  className,
  hasShadow,
  notification,
  mode,
  backgroundColor,
  kioskAuth,
  navAlignment = 'left',
}: Props): Node => {
  const componentTheme = useComponentTheme(theme => theme.components.Header, mode);

  return (
    <Container hasShadow={hasShadow} className={className}>
      {ariaLabel && <h2 className="hide-text">{ariaLabel}</h2>}
      <Nav backgroundColor={backgroundColor} componentTheme={componentTheme} navAlignment={navAlignment}>
        {topNav}
        {kioskAuth}
      </Nav>

      {some(children) && <SubNav>{children}</SubNav>}
      {notification}
    </Container>
  );
};

const Container = styled('header')`
  width: 100%;
  font-size: ${({ theme }) => theme.fontSize.sm};
  background-color: ${themeProp('color.white')};
  z-index: ${themeProp('positioning.header')};
  box-shadow: ${({ theme, hasShadow }) => hasShadow && theme.boxShadow.small};
  padding-top: constant(safe-area-inset-top);
  padding-top: env(safe-area-inset-top);

  @media (${themeProp('breakpoint.tablet')}) {
    font-size: ${({ theme }) => theme.fontSize.md};
  }
`;

const Nav = styled('div')`
  display: flex;
  height: ${({ theme }) => theme.constants.headerHeight};
  padding: 0 24px;
  align-items: center;
  background-color: ${({ backgroundColor, componentTheme }) =>
    backgroundColor || componentTheme.Container.backgroundColor};
  color: ${({ componentTheme }) => componentTheme.Text.color};
  font-family: ${({ theme, componentTheme }) =>
    componentTheme.Text.secondaryFontFamily ? theme.typography.secondaryFontFamily : 'inherit'};
  text-transform: ${({ componentTheme }) => componentTheme.Text.textTransform};
  justify-content: ${({ navAlignment }) => navAlignment === 'right' && 'flex-end'};

  @media (${themeProp('breakpoint.tabletLarge')}) {
    padding: 0 32px;
  }
`;

const SubNav = styled('div')`
  display: flex;
  height: ${({ theme }) => theme.constants.headerHeight};
  border-top: ${themeProp('stroke.light')};
  align-items: center;
`;
